import React, { useEffect, useState, useCallback } from "react";
import { fetchIntegrationsHealth, resolveFailure } from "../lib/api";

const PROVIDER_LABEL = {
  sendgrid: "SendGrid", gohighlevel: "GoHighLevel", ghl: "GoHighLevel",
  stripe: "Stripe", twilio: "Twilio", mailchimp: "Mailchimp",
  apollo: "Apollo", windsor_ai: "Windsor.ai", google_maps: "Google Maps",
};
const label = (p) => PROVIDER_LABEL[p] || p;

const WINDOWS = [
  { label: "24h", hours: 24 },
  { label: "7 days", hours: 168 },
  { label: "30 days", hours: 720 },
];

const IntegrationsHealthPanel = () => {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(168);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await fetchIntegrationsHealth(hours);
      setData(d);
    } catch (e) {
      setData({ error: e?.message || "Failed to load" });
    }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const onResolve = async (id) => {
    setBusy(true);
    try {
      await resolveFailure(id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div data-testid="health-loading" style={{ padding: 20, color: "#5a5d96" }}>Loading integration health…</div>;

  const isHealthy = (data.unresolved_count || 0) === 0;

  return (
    <div data-testid="integrations-health-panel" style={{
      marginTop: 26, padding: 22, borderRadius: 10,
      background: "#fff", border: `2px solid ${isHealthy ? "#1A7A4A" : "#A32D2D"}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h2 className="font-display" style={{ fontSize: 30, color: "#0A1628", margin: 0, letterSpacing: 1 }}>
          INTEGRATION HEALTH
          {isHealthy ? (
            <span data-testid="health-badge" style={badge("#1A7A4A")}>ALL CLEAR</span>
          ) : (
            <span data-testid="health-badge" style={badge("#A32D2D")}>
              {data.unresolved_count} UNRESOLVED
            </span>
          )}
        </h2>
        <div style={{ display: "flex", gap: 6 }}>
          {WINDOWS.map(w => (
            <button key={w.hours} onClick={() => setHours(w.hours)}
              data-testid={`health-window-${w.hours}`}
              style={tab(hours === w.hours)}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <p style={{ color: "#5a5d96", margin: "6px 0 18px", fontSize: 15 }}>
        Every silent failure in SendGrid · GHL · Stripe · Twilio · Mailchimp lands here.
        Resolve once fixed at the source — the count drops, OKONKO morning brief stays green.
      </p>

      {/* By Provider summary cards */}
      {data.by_provider && data.by_provider.length > 0 ? (
        <div data-testid="health-by-provider" style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12, marginBottom: 18,
        }}>
          {data.by_provider.map((p) => (
            <div key={p.provider} data-testid={`health-provider-${p.provider}`} style={{
              padding: "12px 14px", background: "#F8F9FA", borderRadius: 8, borderLeft: "4px solid #A32D2D",
            }}>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 18, letterSpacing: 1, color: "#0A1628" }}>
                {label(p.provider)}
              </div>
              <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 38, lineHeight: 1, color: "#A32D2D" }}>
                {p.count}
              </div>
              <div style={{ fontSize: 12, color: "#5a5d96" }}>failures in window</div>
            </div>
          ))}
        </div>
      ) : (
        <div data-testid="health-empty" style={{ padding: 20, background: "#D6F0E0", borderRadius: 8, color: "#1A7A4A" }}>
          ✓ No integration failures recorded in this window. SendGrid + GHL + Stripe webhooks all firing cleanly.
        </div>
      )}

      {/* Failure summary table */}
      {data.summary && data.summary.length > 0 && (
        <>
          <h4 style={{ marginTop: 18, marginBottom: 8, color: "#0A1628" }}>Failure Patterns ({data.summary.length})</h4>
          <div data-testid="health-summary-table" style={{
            background: "#fff", border: "1px solid #e5e7ee", borderRadius: 8, overflow: "hidden",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#F8F9FA" }}>
                  <th style={th}>Provider</th>
                  <th style={th}>Operation</th>
                  <th style={th}>Count</th>
                  <th style={th}>Last error</th>
                </tr>
              </thead>
              <tbody>
                {data.summary.map((s, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #eef0f4" }}>
                    <td style={td}><strong>{label(s.provider)}</strong></td>
                    <td style={td}><code style={{ fontSize: 13 }}>{s.operation}</code></td>
                    <td style={{ ...td, fontFamily: "Bebas Neue, sans-serif", fontSize: 22, color: "#A32D2D" }}>{s.count}</td>
                    <td style={{ ...td, color: "#5a5d96", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.last_error || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Recent failures list */}
      {data.recent && data.recent.length > 0 && (
        <>
          <h4 style={{ marginTop: 22, marginBottom: 8, color: "#0A1628" }}>
            Recent ({data.recent.length})
          </h4>
          <div data-testid="health-recent-list" style={{ maxHeight: 360, overflowY: "auto" }}>
            {data.recent.map((f) => (
              <div key={f.id} data-testid={`health-failure-${f.id}`} style={{
                padding: "12px 14px", borderBottom: "1px solid #eef0f4",
                display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center",
                opacity: f.resolved ? 0.45 : 1,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div>
                    <strong style={{ color: "#0A1628" }}>{label(f.provider)}</strong>
                    <span style={{ color: "#5a5d96", marginLeft: 8 }}>·</span>
                    <code style={{ marginLeft: 8, fontSize: 13 }}>{f.operation}</code>
                    {f.resolved && <span style={pill("#1A7A4A")}>RESOLVED</span>}
                  </div>
                  <div style={{ fontSize: 13, color: "#5a5d96", marginTop: 4 }}>
                    {f.error_message || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#8a8d96", marginTop: 4 }}>
                    {new Date(f.created_at).toLocaleString()} · context: {JSON.stringify(f.context || {}).slice(0, 140)}
                  </div>
                </div>
                {!f.resolved && (
                  <button
                    data-testid={`health-resolve-${f.id}`}
                    disabled={busy}
                    onClick={() => onResolve(f.id)}
                    className="btn-outline-navy tap"
                    style={{ fontSize: 13, padding: "8px 14px", minHeight: 38 }}
                  >
                    MARK RESOLVED
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const badge = (color) => ({
  marginLeft: 12, padding: "4px 10px", borderRadius: 4,
  background: color, color: "#fff", fontSize: 12,
  fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
  verticalAlign: "middle",
});

const tab = (active) => ({
  fontFamily: "Bebas Neue, sans-serif", fontSize: 14, letterSpacing: 1.5,
  padding: "8px 14px", borderRadius: 6, cursor: "pointer", minHeight: 36,
  background: active ? "#0A1628" : "#fff",
  color: active ? "#fff" : "#0A1628",
  border: `2px solid ${active ? "#0A1628" : "#e5e7ee"}`,
});

const pill = (color) => ({
  marginLeft: 10, padding: "2px 8px", borderRadius: 3, background: color,
  color: "#fff", fontSize: 10, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1,
});

const th = { textAlign: "left", padding: "10px 14px", fontSize: 12, letterSpacing: 1, color: "#5a5d96", textTransform: "uppercase" };
const td = { padding: "10px 14px", fontSize: 14, verticalAlign: "top" };

export default IntegrationsHealthPanel;
